import { ThemeProvider } from '@mui/material/styles';
import { businessData as businessDataAtom, userDetails as userDetailsAtom } from 'atoms';
import SubHeading from 'common/SubHeading';
import { formatDate } from 'common/formatters';
import { materialTheme } from 'materialTheme';
import InvoiceTable from 'pages/Payment/Invoice/InvoiceTable';
import CreateInvoice from 'pages/Payment/Invoice/InvoiceTable/Partials/CreateInvoice';
import FiltersBar from 'pages/Payment/Invoice/InvoiceTable/Partials/FiltersBar.jsx';
import SortDropdown from 'pages/Payment/Invoice/InvoiceTable/Partials/SortDropdown';
import { Search, SlidersHorizontal } from 'lucide-react';
import { useRecoilState } from 'recoil';
import { useAuth } from 'context/AuthContext';
import ConfirmDeleteInvoiceModal from 'common/ConfirmDeleteInvoiceModal';
import React, { useEffect, useRef, useState } from 'react';

export const Invoice = () => {
    const { token, user: authUser } = useAuth();
    const [business, setBusiness] = useRecoilState(businessDataAtom);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useRecoilState(userDetailsAtom);
    const [providers, setProviders] = useState([]);
    const [rawProviders, setRawProviders] = useState([]);
    const [providerFullDetails, setProviderFullDetails] = useState([]);
    const providerFullDetailsRef = useRef([]);
    const [servicesList, setServicesList] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [selectedProviderId, setSelectedProviderId] = useState(null);
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [selectedPaymentType, setSelectedPaymentType] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [sortOption, setSortOption] = useState('date_newest');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [invoiceToDeleteId, setInvoiceToDeleteId] = useState(null);
    const [isDeletingInvoice, setIsDeletingInvoice] = useState(false);

    const [invoices, setInvoices] = useState([]);
    const [purchasedServices, setPurchasedServices] = useState([]);

    const getAllPayments = async () => {
        if (!business?.id) return;

        setLoading(true);
       

        try {
            // Validate access token
            if (!token) {
                console.error('No authentication token found');
                setLoading(false);
                return;
            }

            // Fetch invoices from backend API
            const invoicesResponse = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/get-invoices`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ business_id: business.id, isDeleted: 'false' })
                }
            );

            const invoicesResult = await invoicesResponse.json();
            let invoicesData = [];
            let invoicesError = null;

            if (!invoicesResponse.ok || invoicesResult.error) {
                invoicesError = invoicesResult.error || 'Failed to fetch invoices';
                console.error('Error fetching invoices:', invoicesError);
            } else if (invoicesResult.success && invoicesResult.data !== undefined) {
                invoicesData = Array.isArray(invoicesResult.data) ? invoicesResult.data : [];
            }

            // Fetch purchased services from backend API
            const purchasedServicesResponse = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/purchased_services`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ business_id: business.id })
                }
            );

            const purchasedServicesResult = await purchasedServicesResponse.json();
            let purchasedServicesData = [];
            let purchasedServicesError = null;

            if (!purchasedServicesResponse.ok || purchasedServicesResult.error) {
                purchasedServicesError = purchasedServicesResult.error || 'Failed to fetch purchased services';
                console.error('Error fetching purchased services:', purchasedServicesError);
            } else if (purchasedServicesResult.success && purchasedServicesResult.data !== undefined) {
                purchasedServicesData = Array.isArray(purchasedServicesResult.data) ? purchasedServicesResult.data : [];
            }

            const allPurchasedServices = purchasedServicesData || [];

            const allProviderIds = [...new Set(allPurchasedServices.map((ps) => ps.provider_id).filter(Boolean))];
            const allPatientIds = [...new Set(allPurchasedServices.map((ps) => ps.patient_id).filter(Boolean))];

            let providersData = [];
            let patientsData = [];

            if (allProviderIds.length > 0) {
                providersData = allProviderIds
                    .map((id) => {
                        const item = providerFullDetailsRef.current.find(
                            (entry) => String((entry.provider ?? entry).id) === String(id)
                        );
                        return item ? (item.provider ?? item) : null;
                    })
                    .filter(Boolean);
            }

            if (allPatientIds.length > 0) {
                try {
                    const patientsResponse = await fetch(
                        `${process.env.REACT_APP_SERVER_URL}/api/providers/patients`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({ ids: allPatientIds })
                        }
                    );

                    const patientsResult = await patientsResponse.json();
                    if (patientsResponse.ok && patientsResult.success && patientsResult.data !== undefined) {
                        patientsData = Array.isArray(patientsResult.data) ? patientsResult.data : [];
                    } else {
                        console.error('Error fetching patients:', patientsResult.error || 'Failed to fetch patients');
                    }
                } catch (error) {
                    console.error('Error fetching patients:', error);
                }
            }

            const enrichedPurchasedServices = allPurchasedServices.map((ps) => ({
                ...ps,
                provider: providersData?.find((p) => p.id === ps.provider_id) || null,
                patients: patientsData?.find((p) => p.id === ps.patient_id) || null,
            }));

            if (invoicesError) console.error('Error fetching invoices:', invoicesError);
            if (purchasedServicesError) console.error('Error fetching purchased services:', purchasedServicesError);

            setInvoices(invoicesData);
            setPurchasedServices(enrichedPurchasedServices || []);
        } catch (error) {
            console.error('Error fetching payments:', error);
        } finally {
            setLoading(false);
        }
    };

    // Paid invoices are a financial record — the money has already moved, so they
    // must stay on the books. Only unpaid/pending invoices can be deleted.
    const isPaidInvoice = (id) => {
        const invoice = (invoices || []).find((item) => String(item?.id) === String(id));
        return String(invoice?.status || '').trim().toLowerCase() === 'paid';
    };

    const handleDelete = (id) => {
        if (isPaidInvoice(id)) {
            console.warn('Paid invoices cannot be deleted:', id);
            return;
        }
        setInvoiceToDeleteId(id);
        setDeleteConfirmOpen(true);
    };

    const performDelete = async () => {
        const id = invoiceToDeleteId;
        if (!id) return;

        if (isPaidInvoice(id)) {
            console.warn('Paid invoices cannot be deleted:', id);
            setDeleteConfirmOpen(false);
            setInvoiceToDeleteId(null);
            setIsDeletingInvoice(false);
            return;
        }

        try {
            if (!token) {
                console.error('No authentication token found');
                setDeleteConfirmOpen(false);
                setInvoiceToDeleteId(null);
                setIsDeletingInvoice(false);
                return;
            }

            const userEmail = authUser?.email || '';
            const deletedAt = new Date().toISOString();

            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/invoice/${id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        status: 'deleted',
                        isDeleted: true,
                        deletedBy: userEmail,
                        deletedAt,
                    }),
                }
            );

            if (response.ok) {
                setDeleteConfirmOpen(false);
                setInvoiceToDeleteId(null);
                setIsDeletingInvoice(false);
                getAllPayments();
            } else {
                const result = await response.json().catch(() => ({}));
                console.error('Error deleting invoice:', result.error || 'Failed to delete invoice');
                setDeleteConfirmOpen(false);
                setInvoiceToDeleteId(null);
                setIsDeletingInvoice(false);
            }
        } catch (error) {
            console.error('Error deleting invoice:', error);
            setDeleteConfirmOpen(false);
            setInvoiceToDeleteId(null);
            setIsDeletingInvoice(false);
        }
    };

    const handleConfirmDelete = async () => {
        setIsDeletingInvoice(true);
        await performDelete();
    };

    useEffect(() => {
        if (business?.id) {
            getAllPayments();
        }
    }, [business?.id]);

    useEffect(() => {
        const onRefresh = () => {
            if (business?.id) {
                getAllPayments();
            }
        };
        window.addEventListener('invoices:refresh', onRefresh);
        return () => window.removeEventListener('invoices:refresh', onRefresh);
    }, [business?.id]);

    // Only show records originating from the `invoices` table in the Payment Activity table
    useEffect(() => {
        if (!Array.isArray(invoices) || !business) {
            setRows([]);
            return;
        }

        const invoiceRows = invoices.map((invoice) => createInvoiceRow(invoice, business));
        setRows(invoiceRows);
    }, [invoices, business]);

    // Fetch all providers for the business for dropdown
    useEffect(() => {
        const fetchProviders = async () => {
            if (!business?.id || !token) return;
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_SERVER_URL}/api/providers/provider-full-details`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ business_id: business.id }),
                    }
                );

                const result = await response.json();

                if (!response.ok || result.error) {
                    console.error('Error fetching providers:', result.error || 'Failed to fetch providers');
                    return;
                }

                const data = Array.isArray(result.data) ? result.data : (result.data ? [result.data] : []);
                setProviderFullDetails(data);
                providerFullDetailsRef.current = data;

                const rawList = data.map((item) => item.provider ?? item);
                setRawProviders(rawList);

                const providerItems = rawList.map((p) => ({
                    id: String(p.id),
                    first_name: p.first_name || '',
                    last_name: p.last_name || '',
                    name: `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Unnamed Provider',
                    type: 'provider',
                }));

                const businessOption = {
                    id: `business_${business.id}`,
                    first_name: business?.name || 'Business',
                    last_name: '',
                    name: business?.name || 'Business',
                    type: 'business',
                };

                const sortedProviders = providerItems.sort((a, b) => a.name.localeCompare(b.name));
                setProviders([businessOption, ...sortedProviders]);
            } catch (e) {
                console.error('Error fetching providers:', e);
            }
        };
        fetchProviders();
    }, [business?.id, token]);

    useEffect(() => {
        const fetchServices = async () => {
            if (!business?.id || !token) return;
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_SERVER_URL}/api/providers/services`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ business_id: business.id })
                    }
                );
                const result = await response.json();
                if (result.success && result.data) {
                    const services = Array.isArray(result.data) ? result.data : [];
                    setServicesList(services.map((s) => ({ name: s.name, price: s.price })));
                }
            } catch (e) {
                console.error('Error fetching services:', e);
            }
        };
        fetchServices();
    }, [business?.id, token]);

    const normalizeDate = (d) => (d ? new Date(d) : null);
    const fromDateObj = normalizeDate(dateFrom);
    const toDateObj = normalizeDate(dateTo);

    const applyFiltersAndSort = (inputRows) => {
        let list = Array.isArray(inputRows) ? [...inputRows] : [];
        const text = (searchText || '').toLowerCase();

        if (text) {
            list = list.filter((r) =>
                r._customerLower.includes(text) ||
                r._infoLower.includes(text) ||
                r._providerName.includes(text)
            );
        }

        if (selectedProviderId) {
            list = list.filter((r) => String(r._providerId) === String(selectedProviderId));
        }

        if (selectedPaymentType) {
            const target = String(selectedPaymentType).toLowerCase();
            list = list.filter((r) => (r._invoiceTypeKey || '') === target);
        }

        if (selectedStatus) {
            const targetStatus = String(selectedStatus).toLowerCase();
            list = list.filter((r) => (r.status || '').toLowerCase() === targetStatus);
        }

        if (fromDateObj) {
            list = list.filter((r) => r._rawDate && r._rawDate >= fromDateObj);
        }
        if (toDateObj) {
            const end = new Date(toDateObj);
            end.setHours(23, 59, 59, 999);
            list = list.filter((r) => r._rawDate && r._rawDate <= end);
        }

        list.sort((a, b) => {
            switch (sortOption) {
                case 'date_newest':
                    return (b._rawDate?.getTime() || 0) - (a._rawDate?.getTime() || 0);
                case 'date_oldest':
                    return (a._rawDate?.getTime() || 0) - (b._rawDate?.getTime() || 0);
                case 'amount_high':
                    return b._totalNumber - a._totalNumber;
                case 'amount_low':
                    return a._totalNumber - b._totalNumber;
                case 'customer_az':
                    return a._customerLower.localeCompare(b._customerLower);
                case 'customer_za':
                    return b._customerLower.localeCompare(a._customerLower);
                default:
                    return (b._rawDate?.getTime() || 0) - (a._rawDate?.getTime() || 0);
            }
        });

        return list;
    };

    const getActiveFilterCount = () => {
        let count = 0;
        if (selectedProviderId) count++;
        if (dateFrom) count++;
        if (dateTo) count++;
        if (selectedPaymentType) count++;
        if (selectedStatus) count++;
        return count;
    };

    const filteredRows = applyFiltersAndSort(rows);
    const totalItems = filteredRows.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedRows = filteredRows.slice(startIndex, endIndex);
    const showingStart = totalItems > 0 ? startIndex + 1 : 0;
    const showingEnd = Math.min(endIndex, totalItems);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchText, selectedProviderId, dateFrom, dateTo, selectedPaymentType, selectedStatus, sortOption]);

    return (
        <ThemeProvider theme={materialTheme}>
            <div className={'mt-16 relative flex items-center justify-between w-full'} style={{ marginBottom: '20px' }}>
                <div className={'mt-2 flex'}>
                    <SubHeading>Payment Activity</SubHeading>
                </div>
                <div className={'flex gap-2'}>
                    <CreateInvoice getInvoices={getAllPayments} providers={rawProviders} servicesList={servicesList} providerFullDetails={providerFullDetails} />
                </div>
            </div>

            {rows && rows.length > 0 && (
                <div className={'p-lr mt-4 flex items-center gap-3 flex-wrap'}>
                    <div className="flex-1 relative min-w-[150px] payment-search-wrapper">
                        <input
                            type="text"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            placeholder="Search by customer name, email, or phone..."
                            className="w-full h-[35px] border border-[#CBC1B7] text-[#184353] focus:outline-none focus:border-[#FF5F39] focus:bg-white placeholder:text-[#999999] font-['Poppins'] payment-search-input"
                            style={{ fontSize: '12px', fontWeight: 500, paddingLeft: '32px', paddingRight: '12px', backgroundColor: 'rgba(255, 255, 255, 0.4)' }}
                        />
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#184353] payment-search-icon" />
                    </div>

                    <div className="min-w-[150px] flex-shrink">
                        <SortDropdown value={sortOption} onChange={(e) => setSortOption(e.target.value)} />
                    </div>

                    <button
                        onClick={() => setIsFilterDrawerOpen(true)}
                        className="h-[35px] px-4 text-sm font-medium text-[#184353] border border-[#CBC1B7] rounded-[6px] hover:bg-white hover:border-[#FF5F39] flex items-center gap-2 relative font-['Poppins'] transition-colors"
                        style={{ fontSize: '12px', fontWeight: 500, backgroundColor: 'rgba(255, 255, 255, 0.4)' }}
                    >
                        <SlidersHorizontal className="w-4 h-4" />
                        Filters
                        {getActiveFilterCount() > 0 && (
                            <span className="absolute -top-1 -right-1 bg-[#FF5F39] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-['Poppins']">
                                {getActiveFilterCount()}
                            </span>
                        )}
                    </button>
                </div>
            )}

            {totalItems > 0 && (
                <div className={'p-lr mt-6 text-sm text-gray-600'} style={{ marginBottom: '18px' }}>
                    Showing <span className="font-bold">{showingStart}</span>–<span className="font-bold">{showingEnd}</span> of <span className="font-bold">{totalItems}</span> results
                </div>
            )}

            <FiltersBar
                open={isFilterDrawerOpen}
                onClose={() => setIsFilterDrawerOpen(false)}
                providers={providers}
                selectedProviderId={selectedProviderId}
                setSelectedProviderId={setSelectedProviderId}
                dateFrom={dateFrom}
                setDateFrom={setDateFrom}
                dateTo={dateTo}
                setDateTo={setDateTo}
                selectedPaymentType={selectedPaymentType}
                setSelectedPaymentType={setSelectedPaymentType}
                selectedStatus={selectedStatus}
                setSelectedStatus={setSelectedStatus}
            />

            <div className={'p-lr'} style={{ marginTop: 0, paddingTop: 0 }}>
                <InvoiceTable
                    rows={paginatedRows}
                    handleDelete={handleDelete}
                    loading={loading}
                    noDataMessage="You don't have any payment history yet. Once you book your first appointment or create your first invoice, it will show up here."
                    getInvoices={getAllPayments}
                    totalItems={totalItems}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    itemsPerPage={itemsPerPage}
                    sortOption={sortOption}
                    providers={rawProviders}
                    servicesList={servicesList}
                    providerFullDetails={providerFullDetails}
                ></InvoiceTable>
            </div>

            <ConfirmDeleteInvoiceModal
                open={deleteConfirmOpen}
                onClose={() => {
                    setDeleteConfirmOpen(false);
                    setInvoiceToDeleteId(null);
                }}
                onConfirm={handleConfirmDelete}
                isLoading={isDeletingInvoice}
            />
        </ThemeProvider>
    );
};

function coerceNumberValue(value) {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
    }
    if (typeof value === 'string') {
        if (!value.trim()) return 0;
        const cleaned = value.replace(/[^0-9.,-]/g, '').replace(/,/g, '');
        const parsed = Number(cleaned);
        return Number.isFinite(parsed) ? parsed : 0;
    }
    if (typeof value === 'object') {
        if ('amount' in value) return coerceNumberValue(value.amount);
        if ('price' in value) return coerceNumberValue(value.price);
        if ('value' in value) return coerceNumberValue(value.value);
    }
    return 0;
}

function parseServiceDataFlexible(serviceDataLike) {
    if (!serviceDataLike) return [];
    if (Array.isArray(serviceDataLike)) {
        return serviceDataLike.filter(Boolean);
    }
    if (typeof serviceDataLike === 'string') {
        try {
            const parsed = JSON.parse(serviceDataLike);
            return parseServiceDataFlexible(parsed);
        } catch (error) {
            return [];
        }
    }
    if (typeof serviceDataLike === 'object') {
        if (Array.isArray(serviceDataLike.services)) {
            return parseServiceDataFlexible(serviceDataLike.services);
        }
        if (Array.isArray(serviceDataLike.serviceData)) {
            return parseServiceDataFlexible(serviceDataLike.serviceData);
        }
        if (Array.isArray(serviceDataLike.items)) {
            return parseServiceDataFlexible(serviceDataLike.items);
        }

        const objectValues = Object.values(serviceDataLike);
        if (objectValues.length && objectValues.every((val) => Array.isArray(val))) {
            const maxLength = Math.max(...objectValues.map((val) => val.length));
            const combined = [];
            for (let index = 0; index < maxLength; index += 1) {
                const entry = {};
                Object.entries(serviceDataLike).forEach(([key, val]) => {
                    if (Array.isArray(val)) {
                        entry[key] = val[index];
                    }
                });
                combined.push(entry);
            }
            return combined;
        }

        if (objectValues.length && objectValues.every((val) => typeof val === 'object' && val !== null)) {
            return objectValues.flatMap((val) => parseServiceDataFlexible(val));
        }

        return [];
    }
    return [];
}

function createNormalizedServiceEntry(entry, fallbackName = '', fallbackPrice = 0) {
    if (!entry) return null;
    const resolvedName =
        entry.name ??
        entry.serviceName ??
        entry.service_name ??
        entry.title ??
        entry.service_title ??
        fallbackName ??
        '';
    const resolvedPrice =
        entry.price ??
        entry.servicePrice ??
        entry.service_price ??
        entry.amount ??
        entry.total ??
        fallbackPrice ??
        0;

    const numericPrice = coerceNumberValue(resolvedPrice);
    const formattedPrice = numericPrice.toFixed(2);

    return {
        ...entry,
        name: resolvedName,
        serviceName: resolvedName,
        service_name: resolvedName,
        title: entry.title ?? resolvedName,
        price: numericPrice,
        servicePrice: formattedPrice,
        service_price: formattedPrice,
    };
}

function buildEntriesFromNamedValues(record) {
    const nameSources = [
        record?.serviceName,
        record?.serviceNames,
        record?.service_name,
        record?.service_title,
        record?.title,
    ];
    const priceSources = [
        record?.servicePrice,
        record?.servicePrices,
        record?.service_price,
        record?.service_price_total,
        record?.price,
        record?.amount,
        record?.total,
        record?.paid_amount,
        record?.payment_amount,
    ];

    let names = [];
    let prices = [];

    for (const candidate of nameSources) {
        if (Array.isArray(candidate) && candidate.length) {
            names = candidate.filter(Boolean);
            break;
        }
    }
    if (!names.length) {
        const stringCandidate = nameSources.find((candidate) => typeof candidate === 'string' && candidate.trim());
        if (stringCandidate) {
            names = [stringCandidate.trim()];
        }
    }

    for (const candidate of priceSources) {
        if (Array.isArray(candidate) && candidate.length) {
            prices = candidate;
            break;
        }
    }
    if (!prices.length) {
        const scalarCandidate = priceSources.find(
            (candidate) => candidate !== undefined && candidate !== null && candidate !== '',
        );
        if (scalarCandidate !== undefined) {
            prices = [scalarCandidate];
        }
    }

    if (!names.length && !prices.length) {
        return [];
    }

    const length = Math.max(names.length, prices.length, 1);
    const entries = [];

    for (let index = 0; index < length; index += 1) {
        const name = names[index] ?? names[0] ?? '';
        const price = prices[index] ?? prices[0] ?? 0;
        entries.push(
            createNormalizedServiceEntry(
                {
                    name,
                    serviceName: name,
                    service_name: name,
                    price,
                    servicePrice: price,
                    service_price: price,
                },
                name,
                price,
            ),
        );
    }

    return entries;
}

function buildNormalizedServiceData(record) {
    if (!record) return [];

    let entries = parseServiceDataFlexible(record?.service_data);

    if ((!entries || !entries.length) && record?.serviceData) {
        entries = parseServiceDataFlexible(record.serviceData);
    }

    if ((!entries || !entries.length) && record?._originalData) {
        const original = record._originalData;
        entries =
            parseServiceDataFlexible(original?.service_data) || parseServiceDataFlexible(original?.serviceData);
    }

    let normalized = (entries || []).map((entry) => createNormalizedServiceEntry(entry)).filter(Boolean);

    if (!normalized.length) {
        normalized = buildEntriesFromNamedValues(record);
    }

    if (!normalized.length && record?._originalData) {
        normalized = buildEntriesFromNamedValues(record._originalData);
    }

    return normalized.filter(Boolean);
}

function createInvoiceRow(invoice, business) {
    const providerFirst = invoice?.provider?.first_name || '';
    const providerLast = invoice?.provider?.last_name || '';
    const providerName =
        providerFirst || providerLast
            ? `${providerFirst} ${providerLast}`.trim()
            : invoice?.business_id === business?.id
                ? business?.name
                : '-';
    const rawDate = new Date(invoice.date_of_service || invoice.created_at);
    const totalNumber = Number(invoice.total) || 0;
    // Use a synthetic id for business-level invoice rows so they can be filtered
    const providerId =
        invoice?.provider?.id ||
        invoice?.provider_id ||
        (invoice?.business_id === business?.id ? `business_${business.id}` : null);
    const rawInvoiceType = typeof invoice?.invoice_type === 'string' ? invoice.invoice_type.trim() : '';
    const lowerInvoiceType = rawInvoiceType.toLowerCase();
    // Normalize all consultation spelling variants (consultation / consulation / consulatation) to the canonical key
    const consultationVariants = ['consultation', 'consulation', 'consulatation'];
    const invoiceTypeKey = consultationVariants.includes(lowerInvoiceType) ? 'consultation' : lowerInvoiceType;
    const invoiceTypeLabelMap = {
        invoice: 'Invoice',
        consultation: 'Consultation',
        service: 'Service',
    };
    const displayInvoiceType = invoiceTypeKey
        ? (invoiceTypeLabelMap[invoiceTypeKey] || (rawInvoiceType.charAt(0).toUpperCase() + rawInvoiceType.slice(1)))
        : 'Invoice';
    return {
        id: `invoice_${invoice.id}`,
        originalId: invoice.id,
        payment_type: displayInvoiceType,
        _invoiceTypeKey: invoiceTypeKey,
        date: formatDate(invoice.date_of_service || invoice.created_at),
        status: invoice.status,
        customer: invoice.patient_name || '',
        customer_info: invoice.info_source_data || invoice.phone_number || '',
        provider: providerName,
        total: `$${totalNumber.toFixed(2)}`,
        _rawDate: rawDate,
        _providerId: providerId,
        _providerName: providerName?.toLowerCase?.() || '',
        _totalNumber: totalNumber,
        _customerLower: (invoice.patient_name || '').toLowerCase(),
        _infoLower: ((invoice.info_source_data || '') + ' ' + (invoice.phone_number || '')).toLowerCase(),
        _paymentType: 'invoice',
        _originalData: invoice,
    };
}
